/**
 * PricingDisclosure — Shows a customer the effective charges they are signing
 * up for during first-time billing setup, directly above the payment form.
 *
 * Fetches the customer's effective price list (strategy template merged with
 * any customer-specific overrides) and renders the billable SKUs grouped into
 * the ongoing charges and any occasional fees. Display-only — it never blocks
 * the payment form. Renders nothing when there is no billable pricing to show.
 *
 * Props:
 *   customerID: string — the Customer whose effective pricing to display
 */

import React from 'react';
import { Card, Badge } from '../../design-system';
import { formatCurrency } from '../../utils/formatters';
import { useCustomerPriceList } from '../../api/api-billing-settings';
import styles from './PricingDisclosure.module.css';

// Maps a SKU's billingUnit to the noun shown after "per".
const UNIT_LABELS = {
    page: 'page',
    month: 'month',
    event: 'occurrence',
    MTok: 'million AI tokens',
};

function rateLabel(item) {
    const amount = `$${formatCurrency(item.amount)}`;
    const unit = UNIT_LABELS[item.billingUnit] || item.billingUnit || 'unit';
    return `${amount} per ${unit}`;
}

function PriceRow({ item }) {
    return (
        <li className={styles.row}>
            <div className={styles.rowMain}>
                <span className={styles.rowName}>{item.name}</span>
                {item.description && (
                    <span className={styles.rowDesc}>{item.description}</span>
                )}
            </div>
            <div className={styles.rowRate}>
                <span className={styles.rateAmount}>{rateLabel(item)}</span>
                {item.isCustomRate && (
                    <Badge variant="accent" size="sm">Custom rate</Badge>
                )}
            </div>
        </li>
    );
}

export default function PricingDisclosure({ customerID }) {
    const { data, isLoading } = useCustomerPriceList(customerID);

    if (isLoading) {
        return (
            <Card className={styles.container} padding="lg">
                <p className={styles.loading}>Loading pricing details…</p>
            </Card>
        );
    }

    const priceList = data?.priceList || [];
    if (priceList.length === 0) {
        // Nothing billable to disclose (or pricing unavailable) — never block payment.
        return null;
    }

    // Ongoing charges (recurring fees + per-use rates); recurring shown first.
    const primary = priceList
        .filter((i) => i.category === 'recurring' || i.category === 'usage')
        .sort((a, b) => (a.category === 'recurring' ? 0 : 1) - (b.category === 'recurring' ? 0 : 1));

    const occasional = priceList.filter((i) => i.category === 'occasional');

    return (
        <Card className={styles.container} padding="lg">
            <h4 className={styles.heading}>What you'll pay</h4>
            <p className={styles.subhead}>
                Here's a summary of the charges you're agreeing to for this account.
            </p>

            <ul className={styles.list}>
                {primary.map((item) => (
                    <PriceRow key={item.sku} item={item} />
                ))}
            </ul>

            {occasional.length > 0 && (
                <>
                    <h5 className={styles.groupHeading}>Other fees that may apply</h5>
                    <ul className={styles.list}>
                        {occasional.map((item) => (
                            <PriceRow key={item.sku} item={item} />
                        ))}
                    </ul>
                </>
            )}
        </Card>
    );
}
